import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo } from "react";

import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

import { Link } from "react-router-dom";

//import components
import Breadcrumbs from '../../components/Common/Breadcrumb';
import TableContainer from '../../components/Common/DataTableContainer';

import { useDispatch, useSelector } from "react-redux";
import { fetchAneesVouchersData } from "../../store/anees-vouchers/actions";

const AneesVoucher = props => {
  const dispatch = useDispatch();

  const { data, loading, error } = useSelector(state => state.aneesVouchers);

  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleViewClick = (voucher) => {
    setSelectedVoucher(voucher);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedVoucher(null);
  };

  const exportToExcel = () => {
    if (!selectedVoucher || !selectedVoucher.vouchers || selectedVoucher.vouchers.length === 0) {
      return;
    }

    // Prepare CSV data
    const headers = ['#', 'Voucher Key', 'Status', 'Driver Name', 'Driver Phone'];
    const rows = selectedVoucher.vouchers.map((v, index) => [
      index + 1,
      v.generate_key || '',
      v.status || '',
      v.driver?.name || '-',
      v.driver?.phone || '-'
    ]);

    // Convert to CSV format
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Add BOM for UTF-8 Excel compatibility
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `Generated_Vouchers_${selectedVoucher.voucher_id}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    dispatch(fetchAneesVouchersData());
  }, [dispatch]);

  const columns = useMemo(
    () => [
      {
        header: 'Code',
        accessorKey: 'code',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Title',
        accessorKey: 'title',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Start Date',
        accessorKey: 'start_date',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'End Date',
        accessorKey: 'end_date',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Repeatation Numbers',
        accessorKey: 'repeatation_numbers',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Amount',
        accessorKey: 'amount',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Actions',
        accessorKey: 'actions',
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <button
            className="btn btn-sm btn-success"
            onClick={() => handleViewClick(row.original)}
          >
            View
          </button>
        ),
      }
    ],
    []
  );

  const totalRecords = data?.data?.length ?? 0;

  const transformedData = useMemo(() => {
    return data?.data?.map(item => {
      return {
        code: item.voucher_id || "N/A",
        title: item.title || "N/A",
        start_date: item.start_date || "N/A",
        end_date: item.end_date || "N/A",
        repeatation_numbers: item.repeatation_numbers || 1,
        amount: item.amount || "N/A",
        ...item,
      };
    });
  }, [data]);

  document.title = "Anees Vouchers | Moeen";
  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Home" breadcrumbItem="Anees Vouchers" />
        <div className="float-end d-none d-md-block">
          <Link to="/anees-vouchers/create"
            color="primary"
            className="btn btn-primary text-white waves-effect waves-light"
          >
            Add Anees Voucher
          </Link>
        </div>
        {loading ? (
          <Skeleton count={10} height={40} />
        ) : (
          <TableContainer
            columns={columns}
            data={transformedData || []}
            isGlobalFilter={true}
            isPagination={true}
            SearchPlaceholder={`Looking for something? ${totalRecords} results loaded.`}
            pagination="pagination"
            paginationWrapper='dataTables_paginate paging_simple_numbers'
            tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
          />
        )}

        {isModalOpen && selectedVoucher && (
          <>
            <div className="modal-backdrop fade show"></div>

            <div className="modal fade show d-block" tabIndex="-1">
              <div className="modal-dialog modal-dialog-centered modal-xl">
                <div className="modal-content shadow-lg border-0 rounded-3">

                  <div className="modal-header bg-primary text-white">
                    <h5 className="modal-title">
                      🎟️ Voucher: <span className="fw-bold">{selectedVoucher.voucher_id}</span>
                    </h5>
                    <button
                      type="button"
                      className="btn-close btn-close-white"
                      onClick={closeModal}
                    ></button>
                  </div>

                  <div className="modal-body">
                    <div className="row mb-3">
                      <div className="col-md-6 mb-2">
                        <strong>Title:</strong> {selectedVoucher.title}
                      </div>
                      <div className="col-md-6 mb-2">
                        <strong>Amount:</strong> Rs. {selectedVoucher.amount}
                      </div>
                      <div className="col-md-6 mb-2">
                        <strong>Start Date:</strong> {selectedVoucher.start_date}
                      </div>
                      <div className="col-md-6 mb-2">
                        <strong>End Date:</strong> {selectedVoucher.end_date}
                      </div>
                      <div className="col-md-6 mb-2">
                        <strong>Repeatations:</strong> {selectedVoucher.repeatation_numbers}
                      </div>
                      <div className="col-md-6 mb-2">
                        <strong>Status:</strong> Active
                      </div>
                    </div>

                    <hr />

                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">🔐 Generated Vouchers</h6>
                      <button
                        className="btn btn-sm btn-success"
                        onClick={exportToExcel}
                        disabled={!selectedVoucher?.vouchers || selectedVoucher.vouchers.length === 0}
                      >
                        <i className="mdi mdi-file-excel"></i> Export Excel
                      </button>
                    </div>

                    <div
                      style={{
                        maxHeight: "300px",
                        overflowY: "auto",
                        border: "1px solid #dee2e6",
                        borderRadius: "0.5rem",
                      }}
                    >
                      <table className="table table-bordered table-striped mb-0">
                        <thead className="table-light sticky-top">
                          <tr>
                            <th>#</th>
                            <th>Voucher Key</th>
                            <th>Status</th>
                            <th>Driver</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedVoucher.vouchers?.length > 0 ? (
                            selectedVoucher.vouchers.map((v, index) => (
                              <tr key={v.id}>
                                <td>{index + 1}</td>
                                <td>{v.generate_key}</td>
                                <td>
                                  <span
                                    className={`badge ${v.status === "unused"
                                        ? "bg-success"
                                        : "bg-secondary"
                                      }`}
                                  >
                                    {v.status}
                                  </span>
                                </td>
                                <td>{v.driver ? `${v.driver.name} - ${v.driver.phone}` : '-'}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="4" className="text-center">
                                No vouchers found.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={closeModal}>
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

      </div>
    </div>
  );
};

AneesVoucher.propTypes = {
  t: PropTypes.any
};

export default AneesVoucher;